// Limits für Plan-Payments und ihr Zustand. Rein, ohne React und ohne
// helpers/index.js. Legacy und Trials laufen nicht hierüber, sie behalten die
// Rohwerte und usePaymentRestrictions wie bisher.
import {
  PLANS,
  RESOURCES,
  WARNING_RATIO,
  deriveTalentStorage,
  isUnlimitedCount
} from './plans'

// Gemeinsames Enum für alle Meter in Dashboard und Aivy OS
export const LIMIT_STATES = {
  UNKNOWN: 'unknown',
  PENDING: 'pending',
  UNLIMITED: 'unlimited',
  OK: 'ok',
  WARNING: 'warning',
  AT_LIMIT: 'at_limit',
  OVER_LIMIT: 'over_limit'
}

export const LIMIT_SOURCES = {
  PAYMENT: 'payment',
  SENTINEL: 'sentinel',
  PLAN_DEFAULT: 'plan_default',
  DERIVED: 'derived',
  MISSING: 'missing',
  LEGACY: 'legacy'
}

// Ganze Zahl >= 0 oder null. stripehook schreibt Zahlen auch als String.
export const toCount = (value) => {
  const n =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  if (typeof n !== 'number' || Number.isNaN(n) || n < 0) return null
  return n === Infinity ? Infinity : Math.floor(n)
}

// Der Payment-Wert gewinnt, das Paket liefert nur den Default. 0 und null
// heißen keine Info, denn Aivy OS legt Payments mit 0 an und speichert leere
// Felder als 0. Erst ab UNLIMITED gilt ein Wert als unbegrenzt, sonst wäre ein
// Individualvertrag mit 10.000 Talenten still unbegrenzt. Der Talentspeicher
// leitet sich aus dem aufgelösten Stellen-Limit ab (careerLimit). Bei custom
// ohne Wert ist das Limit unbekannt und wird nicht durchgesetzt.
export const resolveLimit = ({ planId, resource, payment, careerLimit }) => {
  const definition = RESOURCES[resource]
  if (!definition) return { limit: null, source: LIMIT_SOURCES.MISSING }

  const raw = toCount(payment?.[definition.field])
  if (raw > 0) {
    return isUnlimitedCount(raw)
      ? { limit: Infinity, source: LIMIT_SOURCES.SENTINEL }
      : { limit: raw, source: LIMIT_SOURCES.PAYMENT }
  }

  const plan = PLANS[planId]
  if (!plan) return { limit: null, source: LIMIT_SOURCES.MISSING }

  if (resource === 'talentStorage') {
    return {
      limit: deriveTalentStorage(planId, careerLimit),
      source: LIMIT_SOURCES.DERIVED
    }
  }

  return { limit: plan.limits[resource], source: LIMIT_SOURCES.PLAN_DEFAULT }
}

// Alle drei Limits in der richtigen Reihenfolge. Zuerst die Stellen, deren
// Ergebnis geht in den Talentspeicher ein.
export const resolvePlanLimits = ({ planId, payment }) => {
  const careers = resolveLimit({ planId, resource: 'careers', payment })
  const seats = resolveLimit({ planId, resource: 'seats', payment })
  const talentStorage = resolveLimit({
    planId,
    resource: 'talentStorage',
    payment,
    careerLimit: careers.limit
  })
  return { careers, seats, talentStorage }
}

// Limits sind inklusiv wie LP gueltig(). Bei 5 Stellen ist die fünfte erlaubt,
// erst die sechste wäre über dem Limit. warning gilt ab 80 % der Grenze.
export const resolveLimitState = ({
  resource,
  limit,
  used = null,
  enforced = false,
  source = null
}) => {
  const usedCount = toCount(used)
  const knownLimit = typeof limit === 'number' && !Number.isNaN(limit)
  const finiteLimit = knownLimit && Number.isFinite(limit)

  let state
  if (!knownLimit) state = LIMIT_STATES.UNKNOWN
  else if (!finiteLimit) state = LIMIT_STATES.UNLIMITED
  else if (usedCount === null) state = LIMIT_STATES.PENDING
  else if (usedCount > limit) state = LIMIT_STATES.OVER_LIMIT
  else if (usedCount === limit) state = LIMIT_STATES.AT_LIMIT
  else if (limit > 0 && usedCount / limit >= WARNING_RATIO)
    state = LIMIT_STATES.WARNING
  else state = LIMIT_STATES.OK

  let remaining = null
  if (knownLimit && !finiteLimit) remaining = Infinity
  else if (finiteLimit && usedCount !== null)
    remaining = Math.max(0, limit - usedCount)

  return {
    resource,
    limit: knownLimit ? limit : null,
    used: usedCount,
    remaining,
    ratio:
      finiteLimit && limit > 0 && usedCount !== null ? usedCount / limit : null,
    state,
    enforced: !!enforced && knownLimit,
    source
  }
}

// Darf delta weitere Einheiten belegen. Fail-open, solange nicht durchgesetzt
// wird, das Limit unbekannt oder unbegrenzt ist oder die Nutzung fehlt.
export const canAdd = (state, delta = 1) => {
  if (!state || !state.enforced) return true
  if (delta <= 0) return true
  if (typeof state.limit !== 'number' || !Number.isFinite(state.limit))
    return true
  if (state.used === null || state.used === undefined) return true
  return state.used + delta <= state.limit
}
